import {FormArray, FormControl, FormGroup} from '@angular/forms';

import {BehaviorSubject, forkJoin, Observable} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    CompanyTaxDetailsService,
    CompanyTaxDetailInterface,
    CountryModel,
    CompanyExtendedModel,
    CompanyTaxDetailModel,
    CompanyTaxDetailInputModel,
    CompanyTaxInfoSectionInput,
} from '@spa/api';

import {DuplicatedValueValidatorKey, InArrayValidator, MaxLengthValidatorKey} from '@spa/validators';

import {BaseControl, CountryAutocompleteControl, Helper} from '../../../../../shared/classes';

import {CompanyTaxDetailsFormErrorStatesEnum} from '../../../enums';

import {
    CompanyTaxDetailsControls,
    CompanyTaxDetailsIdSelector,
    CompanyTaxDetailsCountryIdSelector,
    CompanyTaxDetailsTaxNumberSelector,
    CompanyTaxDetailsFormArraySelector,
    CompanyTaxDetailsTaxNumberMaxLength,
    CompanyVatDetailsGiinSelector,
    CompanyVatDetailsLeiCodeSelector,
    CompanyVatDetailsLeiExpirationDateSelector,
    CompanyVatDetailsVatSelector,
} from '../../../../../../assets/descriptors';
import { CompanyTaxInfoSectionInputInterface } from '@spa/api/lib/interfaces/company-tax-detail';

export class CompanyTaxDetailsForm {
    private readonly company: CompanyExtendedModel;
    private readonly taxDetailsService: CompanyTaxDetailsService;

    public form: FormGroup;
    public controls: Array<BaseControl[]>;
    public controlsObservable: BehaviorSubject<Array<BaseControl[]>>;

    public readonly taxDetailsFormArraySelector: string;

    constructor(
        company: CompanyExtendedModel,
        taxDetailsService: CompanyTaxDetailsService
    ) {
        this.company = company;
        this.taxDetailsService = taxDetailsService;

        this.taxDetailsFormArraySelector = CompanyTaxDetailsFormArraySelector;

        this.controlsObservable = new BehaviorSubject([]);
    }

    private initControls(countries: CountryModel[], model?: CompanyTaxDetailModel): BaseControl[] {
        const controls = CompanyTaxDetailsControls();

        return controls.map((control: BaseControl) => {
            switch (control.name) {
                case CompanyTaxDetailsIdSelector:
                    control.value = !model ? null : model.id;
                    break;
                case CompanyTaxDetailsCountryIdSelector:
                    control.value = !model ? null : model.countryId;

                    const countryIds = countries.map((country: CountryModel) => country.id);

                    (control as CountryAutocompleteControl).countries = countries;

                    control.extendValidators(InArrayValidator(countryIds));
                    break;
                case CompanyTaxDetailsTaxNumberSelector:
                    control.value = !model ? null : model.taxNumber;
                    break;
            }

            return control;
        });
    }

    private getFormControlStatuses(
        formGroup: FormGroup,
        formGroupIndex: number,
        formGroupCountryIds: string[],
        messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; }
    ): boolean[] {
        return Object.keys(formGroup.controls).map((controlKey: string, controlIndex: number) => {
            const formControl = formGroup.controls[controlKey];
            const control = this.controls[formGroupIndex][controlIndex];

            if (formControl.invalid) {
                const errors = Object.keys(formControl.errors).map((errorKey: string) => {
                    if (errorKey === MaxLengthValidatorKey) {
                        return messageFunction(control.placeholder, CompanyTaxDetailsTaxNumberMaxLength)[errorKey];
                    }

                    return messageFunction(control.placeholder)[errorKey];
                });

                control.setErrors(errors);

                return false;
            }

            if (control.name === CompanyTaxDetailsCountryIdSelector) {
                let isValidCountry = true;

                const matchedIndex = formGroupCountryIds.indexOf(control.value as string);

                if ((matchedIndex !== formGroupIndex) && control.value) {
                    control.setError(messageFunction(control.placeholder)[DuplicatedValueValidatorKey]);

                    isValidCountry = false;
                }

                return isValidCountry;
            }

            return true;
        });
    }

    private updateTaxDetails(formArray: FormArray): Observable<ApiResponse<ApiError[] | boolean>> {
        const modelsForUpdate = formArray.controls.filter((formGroup: FormGroup) => {
            return Object.keys(formGroup.controls).filter((formControlKey: string) => {
                return (formControlKey === CompanyTaxDetailsIdSelector) &&
                    formGroup.controls[formControlKey].value;
            }).length > 0;
        }).filter((formGroup: FormGroup) => {
            const filteredTaxDetails = this.company
                .taxDetails.filter((detail: CompanyTaxDetailModel) => {
                    return detail.id === formGroup.controls[CompanyTaxDetailsIdSelector].value;
                });

            if (filteredTaxDetails.length === 0) {
                return false;
            }

            const valuesCheck = Object.keys(formGroup.controls).map((formControlKey: string) => {
                const formControlValue = formGroup.controls[formControlKey].value;

                switch (formControlKey) {
                    case CompanyTaxDetailsIdSelector:
                        return filteredTaxDetails[0].id === formControlValue;
                    case CompanyTaxDetailsCountryIdSelector:
                        return filteredTaxDetails[0].countryId === formControlValue;
                    case CompanyTaxDetailsTaxNumberSelector:
                        return filteredTaxDetails[0].taxNumber === formControlValue;
                    default:
                        return false;
                }
            });

            return valuesCheck.indexOf(false) >= 0;
        }).map((formGroup: FormGroup) => {
            const filteredTaxDetails = this.company.taxDetails.filter((detail: CompanyTaxDetailModel) => {
                return detail.id === formGroup.controls[CompanyTaxDetailsIdSelector].value;
            });

            filteredTaxDetails[0].countryId = formGroup.controls[CompanyTaxDetailsCountryIdSelector].value;
            filteredTaxDetails[0].taxNumber = formGroup.controls[CompanyTaxDetailsTaxNumberSelector].value;

            return filteredTaxDetails[0];
        });

        if (modelsForUpdate.length > 0) {
            return this.createUpdateRequest(modelsForUpdate);
        }

        return Helper.createSuccessResponseObservable<boolean>(true);
    }

    private createUpdateRequest(models: CompanyTaxDetailModel[]): Observable<ApiResponse<ApiError[] | boolean>> {
        return this.taxDetailsService.updateMany(models)
            .pipe(
                switchMap((updateResponse: ApiResponse<ApiError[] | null>) => {
                    if (updateResponse.status !== HttpStatusCodesEnum.Ok) {
                        return this.taxDetailsService.getAllByCompanyId(this.company.id)
                            .pipe(
                                map((getResponse: ApiResponse<ApiError[] | CompanyTaxDetailInterface[]>) => {
                                    if (getResponse.status === HttpStatusCodesEnum.Ok) {
                                        const data = getResponse.data as CompanyTaxDetailInterface[];

                                        this.company.taxDetails = data.map((item: CompanyTaxDetailInterface) => {
                                            return new CompanyTaxDetailModel(item);
                                        });
                                    }

                                    return Helper.errorsHelper().createErrorResponse(
                                        updateResponse,
                                        CompanyTaxDetailsFormErrorStatesEnum.UpdateTaxDetailsFailed
                                    );
                                })
                            );
                    }

                    return Helper.createSuccessResponseObservable<boolean>(true);
                })
            );
    }

    private registerTaxDetails(formArray: FormArray): Observable<ApiResponse<ApiError[] | boolean>> {
        const modelsForRegistration = formArray.controls.filter((formGroup: FormGroup) => {
            return Object.keys(formGroup.controls).filter((formControlKey: string) => {
                return (formControlKey === CompanyTaxDetailsIdSelector) &&
                    !formGroup.controls[formControlKey].value;
            }).length > 0;
        }).map((formGroup: FormGroup) => {
            return new CompanyTaxDetailInputModel(
                formGroup.controls[CompanyTaxDetailsCountryIdSelector].value,
                formGroup.controls[CompanyTaxDetailsTaxNumberSelector].value,
                this.company.id
            );
        });

        if (modelsForRegistration.length > 0) {
            return this.taxDetailsService.registerMany(modelsForRegistration)
                .pipe(
                    map((response: ApiResponse<ApiError[] | CompanyTaxDetailInterface[]>) => {
                        if (response.status !== HttpStatusCodesEnum.Ok) {
                            return Helper.errorsHelper().createErrorResponse<CompanyTaxDetailInterface[]>(
                                response,
                                CompanyTaxDetailsFormErrorStatesEnum.TaxDetailsRegistrationFailed
                            );
                        }

                        const data = response.data as CompanyTaxDetailInterface[];
                        const models = data.map((item: CompanyTaxDetailInterface) => new CompanyTaxDetailModel(item));

                        this.company.taxDetails.push(...models);

                        return Helper.createSuccessResponse<boolean>(true);
                    })
                );
        }

        return Helper.createSuccessResponseObservable<boolean>(true);
    }

    addTaxDetail(countries: CountryModel[]): void {
        if (this.controls.length < 9) {
            const controls = this.initControls(countries).map((control: BaseControl) => {
                control.placeholder = control.placeholder + ' #' + this.controls.length;

                return control;
            });

            const formGroup = new FormGroup({});

            controls.map((control: BaseControl) => {
                formGroup.addControl(control.name, new FormControl(control.value, control.validators));
            });

            (this.form.controls[this.taxDetailsFormArraySelector] as FormArray).push(formGroup);

            this.controls.push(controls);
        }
    }

    canAddTaxDetail(): boolean {
        return this.controls && (this.controls.length < 9);
    }

    initForm(countries: CountryModel[]): void {
        this.form = new FormGroup({});
        this.controls = [];

        if (this.company && this.company.taxDetails && (this.company.taxDetails.length > 0)) {
            this.controls = this.company.taxDetails.map((taxDetail: CompanyTaxDetailModel) => {
                return this.initControls(countries, taxDetail);
            });
        } else {
            const controls = this.initControls(countries);

            this.controls.push(controls);
        }

        const formArray = new FormArray([]);

        this.controls.map((controls: BaseControl[]) => {
            const formGroup = new FormGroup({});

            controls.map((control: BaseControl) => {
                formGroup.addControl(control.name, new FormControl(control.value, control.validators));
            });

            formArray.push(formGroup);
        });

        this.form.addControl(this.taxDetailsFormArraySelector, formArray);

        this.controlsObservable.next(this.controls);
    }

    validateForm(
        countries: CountryModel[],
        messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; }
    ): boolean {
        const emptyFormGroupIndexes = [];

        const formArray = (this.form.controls[this.taxDetailsFormArraySelector] as FormArray);

        const formGroupCountryIds = formArray.controls.map((formGroup: FormGroup) => {
            return Object.keys(formGroup.controls)
                .filter((controlKey: string) => controlKey === CompanyTaxDetailsCountryIdSelector)
                .map((controlKey: string) => formGroup.controls[controlKey].value);
        });

        const statuses = formArray.controls.map((formGroup: FormGroup, formGroupIndex: number) => {
            const emptyGroupCheck = Object.keys(formGroup.controls)
                .filter((controlKey: string) => controlKey !== CompanyTaxDetailsIdSelector)
                .map((controlKey: string) => !formGroup.controls[controlKey].value);

            if ((emptyGroupCheck.indexOf(false) < 0) && (formGroupIndex > 0)) {
                emptyFormGroupIndexes.push(formGroupIndex);

                return true;
            }

            const formControlStatuses = this.getFormControlStatuses(
                formGroup,
                formGroupIndex,
                [].concat(...formGroupCountryIds),
                messageFunction
            );

            return formControlStatuses.indexOf(false) < 0;
        });

        if (emptyFormGroupIndexes.length !== 0) {
            emptyFormGroupIndexes.sort((previousIndex: number, nextIndex: number) => {
                return nextIndex - previousIndex;
            }).map((index: number) => {
                formArray.removeAt(index);

                this.controls.splice(index, 1);
            });
        }

        return statuses.indexOf(false) < 0;
    }

    createRequest(): Observable<ApiResponse<ApiError[] | boolean>> {
        if (this.form.dirty) {
            const requests = [];
            const formArray = this.form.controls[this.taxDetailsFormArraySelector] as FormArray;

            if (this.company.taxDetails && (this.company.taxDetails.length > 0)) {
                const updateRequest = this.updateTaxDetails(formArray);

                if (updateRequest) {
                    requests.push(updateRequest);
                }
            }

            const registrationRequest = this.registerTaxDetails(formArray);

            if (registrationRequest) {
                requests.push(registrationRequest);
            }

            if (requests.length !== 0) {
                return forkJoin(requests)
                    .pipe(
                        map((responses: ApiResponse<ApiError[] | boolean>[]) => {
                            if (Helper.errorsHelper().checkForErrorResponses<boolean>(responses)) {
                                return Helper.errorsHelper().createReducedErrorResponse<boolean>(responses);
                            }

                            return Helper.createSuccessResponse<boolean>(true);
                        })
                    );
            }
        }

        return Helper.createSuccessResponseObservable<boolean>(true);
    }

    createRequestWithVatInfo(vatDetailsForm: FormGroup): Observable<ApiResponse<ApiError[] | boolean>> {
        const companyTaxDetailArray = this.form.value[CompanyTaxDetailsFormArraySelector];
        const companyTaxDetailsFormArray: CompanyTaxDetailInputModel[] = [];
        if (this.form.dirty && companyTaxDetailArray?.length > 0) {
            for (const element of companyTaxDetailArray) {
                const taxDetails = new CompanyTaxDetailInputModel(element.countryId, element.taxNumber, this.company.id,
                    (element.id === null ? 0 : element.id));
                companyTaxDetailsFormArray.push(taxDetails);
            }
        }

        const data: CompanyTaxInfoSectionInputInterface = {
            company_id: this.company.id,
            name: this.company.name,
            lei: vatDetailsForm.value[CompanyVatDetailsLeiCodeSelector],
            vat: vatDetailsForm.value[CompanyVatDetailsVatSelector],
            giin: vatDetailsForm.value[CompanyVatDetailsGiinSelector],
            company_tax_detail: companyTaxDetailsFormArray,
            lei_expiration: vatDetailsForm.value[CompanyVatDetailsLeiExpirationDateSelector],
        };

        const model = new CompanyTaxInfoSectionInput(data);

        return this.taxDetailsService.updateTaxInfoSection(model).pipe(
            map((response: ApiResponse<ApiError[] | CompanyTaxInfoSectionInputInterface>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse<CompanyTaxInfoSectionInputInterface>(
                        response,
                        CompanyTaxDetailsFormErrorStatesEnum.TaxWithVatInfoUpdateFailed
                    );
                }
                const updatedModel = (response.data as CompanyTaxInfoSectionInputInterface);
                this.company.lei = updatedModel.lei;
                this.company.leiExpiration = updatedModel.lei_expiration;
                this.company.giin = updatedModel.giin;
                this.company.vat = updatedModel.vat;
                this.company.taxDetails = updatedModel.company_tax_detail.map((item: CompanyTaxDetailInterface) => {
                    return new CompanyTaxDetailModel(item);
                });

                return Helper.createSuccessResponse<boolean>(true);
            })
        );
    }
}
